/**
 * Populate prefill policy — which source tiers may fill each schema key.
 *
 * Tier precedence (highest first):
 *   user → doc → sed → dam → research → car
 */
import type { FieldAlignmentKind } from './requirementFieldAlignment';
import { getFieldAlignment } from './requirementFieldAlignment';

export type PopulateSourceTier = 'user' | 'doc' | 'sed' | 'dam' | 'research' | 'car';

/** Tiers that may write into authored fields via Populate (excluding user). */
const INFERENCE_TIERS: readonly PopulateSourceTier[] = [
  'doc',
  'sed',
  'dam',
  'research',
  'car',
];

export function isDocOnlyField(requirementId: string, schemaKey: string): boolean {
  return getFieldAlignment(requirementId, schemaKey)?.kind === 'doc-only';
}

export function canPopulateFromTier(
  requirementId: string,
  schemaKey: string,
  tier: PopulateSourceTier,
): boolean {
  if (tier === 'user') return true;
  const alignment = getFieldAlignment(requirementId, schemaKey);
  if (!alignment) {
    // Unregistered keys — sed + car + doc. `doc` joined the default in the
    // extraction program (D5): most extract-schema keys have no alignment
    // entry, so the old sed/car-only default silently discarded every value
    // the extractor produced for them — the stamp filter ate the machine's
    // work with no trace. Alignment registration remains the way to RESTRICT
    // a key; absence of registration must not mean "docs can't fill this".
    return tier === 'sed' || tier === 'car' || tier === 'doc';
  }
  if (alignment.kind === 'doc-only') {
    return tier === 'doc';
  }
  if (alignment.kind === 'transform') {
    return tier === 'doc' || tier === 'car' || tier === 'sed';
  }
  // direct / alias
  switch (tier) {
    case 'doc':
    case 'sed':
    case 'dam':
    case 'research':
      return true;
    case 'car':
      return false;
    default:
      return false;
  }
}

export function allowedPopulateTiers(
  requirementId: string,
  schemaKey: string,
): PopulateSourceTier[] {
  const tiers: PopulateSourceTier[] = ['user'];
  for (const tier of INFERENCE_TIERS) {
    if (canPopulateFromTier(requirementId, schemaKey, tier)) tiers.push(tier);
  }
  return tiers;
}

export function blockedPopulateTiers(
  requirementId: string,
  schemaKey: string,
): PopulateSourceTier[] {
  return INFERENCE_TIERS.filter(t => !canPopulateFromTier(requirementId, schemaKey, t));
}

export interface FieldPopulateSourceAudit {
  requirementId: string;
  schemaKey: string;
  alignmentKind: FieldAlignmentKind | 'unregistered';
  allowedTiers: PopulateSourceTier[];
  blockedTiers: PopulateSourceTier[];
}
